import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from './AdminAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, Lock, Shield, ArrowRight } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const TRUSTBRIDGE_ICON = "https://customer-assets.emergentagent.com/job_flowsecure-fintech/artifacts/jw6e8f7s_7B7D7191-078E-4AB2-A5E6-D847E0D60608.png";

export const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, isAuthenticated } = useAdminAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Redirect if already authenticated
  if (isAuthenticated) {
    navigate('/admin/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      if (message.includes('Admin access')) {
        toast.error('You do not have admin access');
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setIsGoogleLoading(true);
    loginWithGoogle();
  };

  return (
    <div className="min-h-screen bg-[#001F3F] flex" data-testid="admin-login-page">
      <Toaster richColors position="top-right" />
      
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#002952] to-[#001F3F] p-12 flex-col justify-between border-r border-[#003C83]/50">
        <div className="flex items-center gap-3">
          <img src={TRUSTBRIDGE_ICON} alt="TrustBridge" className="h-12 w-12 object-contain" />
          <span className="text-white font-semibold text-xl">TrustBridge Admin</span>
        </div>
        <div>
          <div className="flex items-center gap-2 text-[#0080FF] mb-4">
            <Shield className="w-6 h-6" />
            <span className="text-sm font-medium uppercase tracking-wider">Secure Operations</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">
            Admin Dashboard
          </h1>
          <p className="text-[#66B2FF] text-lg leading-relaxed">
            Manage merchants, review transactions, and oversee the FlowLedger platform.
          </p>
        </div>
        <div className="text-[#66B2FF] text-sm">
          TrustBridge Technologies • Secure Fintech Operations
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#001F3F]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img src={TRUSTBRIDGE_ICON} alt="TrustBridge" className="h-12 w-12 mx-auto mb-2" />
            <span className="text-white font-semibold text-lg">TrustBridge Admin</span>
          </div>

          <div className="bg-[#002952] rounded-2xl p-8 border border-[#003C83]/50">
            <h2 className="text-2xl font-semibold text-white mb-2">Admin Sign In</h2>
            <p className="text-[#66B2FF] mb-8">Access the TrustBridge admin panel</p>

            {/* Google Login */}
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 mb-6 bg-[#003C83] border-[#003C83] text-white hover:bg-[#00479E]"
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              data-testid="admin-google-login-btn"
            >
              {isGoogleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#003C83]" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#002952] text-[#66B2FF]">or with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#66B2FF]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#66B2FF]" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12 bg-[#003C83] border-[#003C83] text-white placeholder:text-[#66B2FF]"
                    placeholder="admin@trustbridgeflow.com"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#66B2FF]">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#66B2FF]" />
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 h-12 bg-[#003C83] border-[#003C83] text-white placeholder:text-[#66B2FF]"
                    placeholder="••••••••"
                    required
                    data-testid="admin-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 bg-[#0080FF] hover:bg-[#66B2FF] text-white"
                disabled={isLoading}
                data-testid="admin-login-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Sign In to Admin
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-[#003C83]/30 rounded-lg">
              <p className="text-[#66B2FF] text-sm text-center">
                Admin access is restricted. Contact a Super Admin if you need access.
              </p>
            </div>
          </div>

          <p className="text-center text-[#66B2FF] text-sm mt-6">
            <Link to="/" className="hover:text-white">
              ← Back to TrustBridge website
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
